import React, { useCallback } from 'react'
import log from 'loglevel'
import { Dropdown } from 'react-bootstrap'
import { Risk } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { generatePermalink } from 'src/components/PageLayout/Permalink'
import { useParams } from 'react-router-dom'
import { Changelog } from 'src/InfoPanel/Changelog'
import { useInfoPanelContext } from 'src/InfoPanel'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { VendorRecord } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { E2E_IDS } from 'src/Utils/testIds'
import { useCurrentControl } from 'src/context/FormContext'
import {
  useUnlinkVendorRiskModal,
  useVendor,
} from 'src/pages/Vendors/vendors.hooks'
import {
  BasicDropdownMenuItem,
  BasicDropdownMenu,
} from 'src/components/Reusable/BasicDropdown/BasicDropdown.styles'
import { ReactComponent as VerticalEllipsesIcon } from '../../../components/Icons/assets/vertical-ellipses-icon.svg'
import { DropdownToggleButton } from '../../../components/Reusable/Buttons'
import {
  useDeleteRiskModal,
  useUnlinkControlFromRiskModal,
} from '../modals/RiskModals.hooks'

// TODO: Can consolidate with CopyLink in Controls
const CopyLink = ({ riskId }: { riskId: string }) => {
  const params = useParams()

  const onCopyLink = useCallback(
    async (e: React.MouseEvent) => {
      try {
        e.preventDefault()
        await navigator.clipboard.writeText(
          generatePermalink({
            modelType: MODEL_TYPE.RISK,
            pageContext: params.pageContext as string,
            modelId: riskId,
            isInternalLink: false,
          }),
        )
      } catch (err) {
        log.error('Failed to copy link to clipboard', err)
      }
    },
    [riskId, params.pageContext],
  )

  return (
    <BasicDropdownMenuItem eventKey="1" onClick={onCopyLink}>
      Copy Permalink
    </BasicDropdownMenuItem>
  )
}

const DeleteRisk = ({ risk }: { risk: Risk.AsObject }): JSX.Element => {
  const showModal = useDeleteRiskModal(risk)

  return (
    <BasicDropdownMenuItem
      eventKey="2"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        showModal()
      }}
    >
      Delete Risk
    </BasicDropdownMenuItem>
  )
}

const ViewActivities = ({ riskId }: { riskId: string }): JSX.Element => {
  const { setInfoPanelState } = useInfoPanelContext()

  const onViewActivities = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setInfoPanelState({
        isShown: true,
        render: <Changelog subjectIds={[riskId]} />,
      })
    },
    [riskId, setInfoPanelState],
  )

  return (
    <BasicDropdownMenuItem eventKey="3" onClick={onViewActivities}>
      View Activities
    </BasicDropdownMenuItem>
  )
}

export const RiskRowMenu = ({ risk }: { risk: Risk.AsObject }): JSX.Element => {
  const { controlModelId } = useParams()
  const { vendorId } = useParams()
  const isControlPage = controlModelId && controlModelId.length
  const isVendorPage = vendorId && vendorId.length

  return isControlPage ? (
    <ControlRiskRowMenu risk={risk} />
  ) : isVendorPage ? (
    <RiskVendorRowMenu risk={risk} vendorId={vendorId} />
  ) : (
    <RiskPageRiskRowMenu risk={risk} />
  )
}

const RiskPageRiskRowMenu = ({
  risk,
}: {
  risk: Risk.AsObject
}): JSX.Element => {
  const riskId = risk.id
  const canDeleteRisk = useCanPerformAction<Risk.AsObject>(
    CAPABILITY.CAN_DELETE_RISKS,
    risk,
  )

  return (
    <Dropdown
      id={E2E_IDS.RISK_ROW_MENU(riskId)}
      data-testid={E2E_IDS.RISK_ROW_MENU(riskId)}
    >
      <Dropdown.Toggle as={DropdownToggleButton}>
        <VerticalEllipsesIcon />
      </Dropdown.Toggle>
      <BasicDropdownMenu>
        <CopyLink riskId={riskId} />
        {canDeleteRisk && <DeleteRisk risk={risk} />}
        <ViewActivities riskId={riskId} />
      </BasicDropdownMenu>
    </Dropdown>
  )
}

export const RiskControlRowMenu = ({
  riskId,
  controlId,
  name,
  isRiskItem,
}: {
  riskId: string
  controlId: string
  name: string
  isRiskItem?: boolean
}): JSX.Element => {
  const unlinkControl = useUnlinkControlFromRiskModal({
    controlId,
    riskId,
    name,
  })

  const text = isRiskItem ? 'Unlink Risk' : 'Unlink Control'

  return (
    <Dropdown
      id={E2E_IDS.RISK_CONTROL_ROW_MENU(riskId)}
      data-testid={E2E_IDS.RISK_CONTROL_ROW_MENU(riskId)}
    >
      <Dropdown.Toggle as={DropdownToggleButton}>
        <VerticalEllipsesIcon />
      </Dropdown.Toggle>
      <BasicDropdownMenu>
        <BasicDropdownMenuItem onClick={unlinkControl}>
          <span>{text}</span>
        </BasicDropdownMenuItem>
      </BasicDropdownMenu>
    </Dropdown>
  )
}

export const RiskVendorRowMenu = ({
  risk,
  vendorId,
}: {
  risk: Risk.AsObject
  vendorId: string
}): JSX.Element => {
  const { data, isLoading, error } = useVendor(vendorId)
  const unlinkRisk = useUnlinkVendorRiskModal(
    data?.getId() || '',
    data?.getName() || '',
    risk.id,
    risk.customId,
    risk.name,
    MODEL_TYPE.VENDOR,
  )
  const vendorObj = data instanceof VendorRecord ? data?.toObject() : undefined
  const canUnlinkVendor = useCanPerformAction<VendorRecord.AsObject>(
    CAPABILITY.CAN_LINK_VENDOR_RISKS,
    vendorObj,
  )
  const canUnlinkRisk = useCanPerformAction<Risk.AsObject>(
    CAPABILITY.CAN_LINK_RISK_VENDORS,
    risk,
  )

  if (!data || isLoading || !canUnlinkVendor || !canUnlinkRisk) {
    return <></>
  } else if (error) {
    log.error(`Error loading vendor id ${vendorId}:`, error)
    return <></>
  }

  return (
    <Dropdown
      id={E2E_IDS.RISK_VENDOR_ROW_MENU(risk.id)}
      data-testid={E2E_IDS.RISK_VENDOR_ROW_MENU(risk.id)}
    >
      <Dropdown.Toggle as={DropdownToggleButton}>
        <VerticalEllipsesIcon />
      </Dropdown.Toggle>
      <BasicDropdownMenu>
        <BasicDropdownMenuItem onClick={unlinkRisk}>
          Unlink Risk
        </BasicDropdownMenuItem>
      </BasicDropdownMenu>
    </Dropdown>
  )
}

const ControlRiskRowMenu = ({ risk }: { risk: Risk.AsObject }): JSX.Element => {
  const { currentControl: control } = useCurrentControl()
  const canUnlinkControl = useCanPerformAction<Control.AsObject>(
    CAPABILITY.CAN_LINK_CONTROL_RISKS,
    control,
  )
  const canUnlinkRisk = useCanPerformAction<Risk.AsObject>(
    CAPABILITY.CAN_LINK_RISK_CONTROLS,
    risk,
  )

  if (!canUnlinkControl || !canUnlinkRisk) {
    return <></>
  }

  return (
    <RiskControlRowMenu
      riskId={risk.id}
      controlId={control.id}
      name={risk.name}
      isRiskItem
    />
  )
}
